/**
 * Owner: Masa Bayu
 * File: qx-writer-sync.ts
 * Date:Time: 2026-10-16 01:10
 * Description: Sync draft editor → tip · paint · figures · picture style.
 */

import { createQXWDocument, type QXWDocument } from '@qx/qxw-core';
import {
  draftSyncFromEditor,
  htmlFromDraftContent,
  readDraftContent,
  withDraftContent,
  type DraftPreviewAsset,
} from './qx-draft-blocks';
import {
  readDraftParagraphGapPt,
  runDraftParagraphGap,
} from './qx-draft-commands';
import {
  applyDraftDropcaps,
  collectDropcapBlockIndexes,
  collectDropcapFontsByBlockIndex,
} from './qx-draft-dropcap';
import {
  enhanceDraftImages,
  type DraftFigureFloat,
  type DraftFigurePicture,
} from './qx-draft-image';
import { adoptTitleFromOutline } from './qx-doc-title';
import { resolveOutlineNodes } from './qx-draft-outline';
import { qxwHasFixedPages } from './qx-import-pdf-manuscript';
import {
  applySpineDocumentPageNumbers,
  htmlFromBookSpineContent,
  resolveSpineFromTip,
  tipUsesSpineSheets,
} from './qx-writer-book-spine';

export type WriterDraftSync = {
  readonly doc: QXWDocument;
  readonly previewByAssetId: Record<string, string>;
  readonly previewWidthByAssetId: Record<string, number>;
  readonly previewHeightByAssetId: Record<string, number>;
  readonly previewFloatByAssetId: Record<string, DraftFigureFloat>;
  readonly previewPictureByAssetId: Record<string, DraftFigurePicture>;
  readonly orderedListBlockIndexes: readonly number[];
  readonly dropcapBlockIndexes: readonly number[];
  readonly dropcapFontsByBlockIndex: Readonly<Record<number, string>>;
};

export function createBlankDocument(): QXWDocument {
  return createQXWDocument({ title: 'Untitled' });
}

export function previewMapFromAssets(
  assets: readonly DraftPreviewAsset[],
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const asset of assets) map[asset.id] = asset.previewSrc;
  return map;
}

export function previewWidthMapFromAssets(
  assets: readonly DraftPreviewAsset[],
): Record<string, number> {
  const map: Record<string, number> = {};
  for (const asset of assets) {
    if (asset.displayWidthPx !== undefined && asset.displayWidthPx > 0) {
      map[asset.id] = asset.displayWidthPx;
    }
  }
  return map;
}

export function previewHeightMapFromAssets(
  assets: readonly DraftPreviewAsset[],
): Record<string, number> {
  const map: Record<string, number> = {};
  for (const asset of assets) {
    if (asset.displayHeightPx !== undefined && asset.displayHeightPx > 0) {
      map[asset.id] = asset.displayHeightPx;
    }
  }
  return map;
}

export function previewFloatMapFromAssets(
  assets: readonly DraftPreviewAsset[],
): Record<string, DraftFigureFloat> {
  const map: Record<string, DraftFigureFloat> = {};
  for (const asset of assets) {
    if (asset.displayFloat) map[asset.id] = asset.displayFloat;
  }
  return map;
}

export function previewPictureMapFromAssets(
  assets: readonly DraftPreviewAsset[],
): Record<string, DraftFigurePicture> {
  const map: Record<string, DraftFigurePicture> = {};
  for (const asset of assets) {
    if (asset.displayPicture) map[asset.id] = asset.displayPicture;
  }
  return map;
}

export function errorDetails(err: unknown): { name: string; message: string } {
  if (err instanceof Error) return { name: err.name, message: err.message };
  return { name: typeof err, message: String(err) };
}

/** True when tip draft has at least one non-empty text/heading/list/figure block. */
export function tipHasPaintableDraft(doc: QXWDocument): boolean {
  const content = readDraftContent(doc);
  if (!content || content.blocks.length === 0) return false;
  return content.blocks.some((block) => {
    if (block.type === 'figure') return true;
    if (block.type === 'list') return block.items.length > 0;
    if ('inlines' in block) {
      return block.inlines.some(
        (inline) => 'text' in inline && inline.text.replace(/\u00a0/g, ' ').trim().length > 0,
      );
    }
    return false;
  });
}

/** HTML for flow draft tip · used to re-paint contentEditable after reload/remount. */
export function htmlFromWriterTip(
  doc: QXWDocument,
  previewByAssetId: Readonly<Record<string, string>> = {},
  orderedListBlockIndexes: readonly number[] = [],
  previewWidthByAssetId: Readonly<Record<string, number>> = {},
  previewFloatByAssetId: Readonly<Record<string, DraftFigureFloat>> = {},
  previewHeightByAssetId: Readonly<Record<string, number>> = {},
  previewPictureByAssetId: Readonly<Record<string, DraftFigurePicture>> = {},
): string {
  const content = readDraftContent(doc);
  if (!content) return '<p><br></p>';
  // Template spine → one visual page sheet per Page N (any bahagian label).
  if (tipUsesSpineSheets(doc)) {
    return htmlFromBookSpineContent(content, resolveSpineFromTip(doc));
  }
  return htmlFromDraftContent(
    content,
    previewByAssetId,
    orderedListBlockIndexes,
    previewWidthByAssetId,
    previewFloatByAssetId,
    previewHeightByAssetId,
    previewPictureByAssetId,
  );
}

/** Paint HTML/plain into editor · gap · images · session dropcaps. */
export function paintWriterEditor(
  node: HTMLElement,
  htmlOrPlain: string,
  asHtml: boolean,
  dropcapBlockIndexes: readonly number[] = [],
  dropcapFontsByBlockIndex: Readonly<Record<number, string>> = {},
  paragraphGapPt?: number,
): void {
  if (asHtml) node.innerHTML = htmlOrPlain;
  else node.innerText = htmlOrPlain;
  const gap =
    typeof paragraphGapPt === 'number' && Number.isFinite(paragraphGapPt) && paragraphGapPt >= 0
      ? Math.round(paragraphGapPt)
      : readDraftParagraphGapPt(node);
  runDraftParagraphGap(gap, node);
  enhanceDraftImages(node);
  applyDraftDropcaps(node, dropcapBlockIndexes, dropcapFontsByBlockIndex);
  applySpineDocumentPageNumbers(node);
}

const EMPTY_SYNC: Omit<WriterDraftSync, 'doc'> = {
  previewByAssetId: {},
  previewWidthByAssetId: {},
  previewHeightByAssetId: {},
  previewFloatByAssetId: {},
  previewPictureByAssetId: {},
  orderedListBlockIndexes: [],
  dropcapBlockIndexes: [],
  dropcapFontsByBlockIndex: {},
};

/** Sync contentEditable → tip blocks · adopt Untitled→H1 · collect Preview assets. */
export function syncDraftFromEditor(
  doc: QXWDocument,
  editor: HTMLDivElement | null,
): WriterDraftSync {
  if (!editor || qxwHasFixedPages(doc)) {
    const nodes = resolveOutlineNodes(editor, doc);
    return {
      doc: adoptTitleFromOutline(
        {
          ...doc,
          metadata: { ...doc.metadata, updatedAt: new Date().toISOString() },
        },
        nodes,
      ),
      ...EMPTY_SYNC,
    };
  }
  const sync = draftSyncFromEditor(editor);
  const assetIds = sync.previewAssets.map((a) => a.id);
  for (const block of sync.content.blocks) {
    if (block.type === 'figure' && block.assetRef) assetIds.push(block.assetRef.id);
  }
  const withBlocks = withDraftContent(doc, sync.content, [...new Set(assetIds)]);
  const nodes = resolveOutlineNodes(editor, withBlocks);
  return {
    doc: adoptTitleFromOutline(withBlocks, nodes),
    previewByAssetId: previewMapFromAssets(sync.previewAssets),
    previewWidthByAssetId: previewWidthMapFromAssets(sync.previewAssets),
    previewHeightByAssetId: previewHeightMapFromAssets(sync.previewAssets),
    previewFloatByAssetId: previewFloatMapFromAssets(sync.previewAssets),
    previewPictureByAssetId: previewPictureMapFromAssets(sync.previewAssets),
    orderedListBlockIndexes: sync.orderedListBlockIndexes,
    dropcapBlockIndexes: collectDropcapBlockIndexes(editor),
    dropcapFontsByBlockIndex: collectDropcapFontsByBlockIndex(editor),
  };
}
